// STALE-DEPLOY-GUARD (2026-07-23)
// Two independent safeguards for the "works only after a manual refresh"
// class of problems that appears when a browser tab stays open across a
// production deploy (the old JS bundle keeps running / a lazy chunk 404s
// because its content-hashed filename changed):
//
//  1. installChunkErrorReload() — global listener that reloads the page ONCE
//     when a dynamically-imported (React.lazy) chunk fails to load. This is
//     the "blank page after clicking a link post-deploy" case.
//
//  2. checkForNewDeploy() — compares the CRA `asset-manifest.json` entry hash
//     captured at startup with the currently-deployed one. When it changed,
//     a new version is live; the caller reloads on the next navigation so the
//     user silently picks up the fresh bundle (fixes the "old bundle behaves
//     wrong until refresh" case, e.g. a missing route polyline).
//
// Both are guarded against reload loops via sessionStorage.

const RELOAD_GUARD_KEY = "mototrips_chunk_reload_ts";
const MANIFEST_URL = "/asset-manifest.json";

// Entry hash captured on first load; set by primeDeployVersion().
let bootEntryKey = null;

function readEntryKey(manifest) {
  if (!manifest) return null;
  // CRA asset-manifest: { "files": {...}, "entrypoints": ["static/js/main.<hash>.js", ...] }
  const eps = manifest.entrypoints;
  if (Array.isArray(eps) && eps.length) {
    return eps.filter((f) => typeof f === "string" && f.endsWith(".js")).join("|");
  }
  const files = manifest.files || {};
  return files["main.js"] || files["runtime~main.js"] || null;
}

async function fetchManifestEntryKey() {
  try {
    // Cache-bust so we always hit the freshly-deployed manifest, not the SW/HTTP cache.
    const res = await fetch(`${MANIFEST_URL}?_=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return null;
    return readEntryKey(await res.json());
  } catch {
    return null;
  }
}

// Call once at startup to remember which bundle version we booted with.
export async function primeDeployVersion() {
  bootEntryKey = await fetchManifestEntryKey();
}

// Returns true when a DIFFERENT bundle version is currently deployed.
export async function checkForNewDeploy() {
  if (!bootEntryKey) return false; // couldn't determine baseline → never force reloads
  const current = await fetchManifestEntryKey();
  return !!current && current !== bootEntryKey;
}

function isChunkLoadError(err, message) {
  const name = err && err.name;
  const msg = (message || (err && err.message) || "").toString();
  return (
    name === "ChunkLoadError" ||
    /Loading chunk [\d]+ failed/i.test(msg) ||
    /Loading CSS chunk/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg)
  );
}

function reloadOnce() {
  // Avoid infinite reload loops: only auto-reload if we haven't done so in the
  // last 10s. If the chunk still fails after that, let the real error surface.
  const now = Date.now();
  const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
  if (now - last < 10000) return;
  sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
  window.location.reload();
}

export function installChunkErrorReload() {
  window.addEventListener("error", (event) => {
    if (isChunkLoadError(event.error, event.message)) reloadOnce();
  });
  window.addEventListener("unhandledrejection", (event) => {
    if (isChunkLoadError(event.reason, event.reason && event.reason.message)) reloadOnce();
  });
}
